/**
 * RefreshButton: read a view's data again now, with how long ago the last read finished ("Updated 12 s ago") and a
 * hint that says what it reads and how often the view reads by itself. The Gateways page and the gateway sheet use
 * it.
 *
 * It stays enabled while it reads (aria-busy and a turning icon), because a disabled button never shows its hint;
 * a click while a read runs does nothing.
 */

import { RefreshCw } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/ui/tooltip";
import { cn } from "@/lib/ui/cn";
import { updatedAgo } from "@/lib/iot/gateway-live";

export interface RefreshButtonProps {
  /** The button's own read runs (not the view's reads by itself, which would make it look busy all the time). */
  busy: boolean;
  onRefresh: () => void;
  /** The hint: what a click reads, and how often the view reads by itself. */
  hint: string;
  /** When the last read finished (epoch ms; 0 when none), for "Updated ... ago". */
  updatedAt?: number;
  /** The clock for "Updated ... ago" (epoch ms). */
  now?: number;
  /** An icon without a label (the sheet's header); the label is "Refresh" otherwise. */
  iconOnly?: boolean;
  /** The accessible name; "Refresh now" by default. */
  ariaLabel?: string;
}

export function RefreshButton(p: RefreshButtonProps) {
  const ago = p.updatedAt && p.now ? updatedAgo(p.updatedAt, p.now) : "";
  return (
    <span className="inline-flex items-center gap-1.5">
      {ago && !p.iconOnly && (
        <span className="text-[11px] text-on-surface-muted" aria-live="off">
          Updated {ago}
        </span>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size={p.iconOnly ? "icon" : "sm"}
            aria-label={p.ariaLabel ?? "Refresh now"}
            aria-busy={p.busy}
            onClick={() => {
              if (!p.busy) p.onRefresh();
            }}
          >
            <RefreshCw className={cn("size-4", p.busy && "animate-spin")} />
            {!p.iconOnly && "Refresh"}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[18rem]">
          {p.hint}
          {ago && p.iconOnly && <span className="mt-0.5 block text-on-surface-muted">Updated {ago}.</span>}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}
